import { FunctionComponent, ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { translationKeys } from "../../../../../models/app-translation-keys";
import { UploadFileListItem } from "../../../../../models/classes/upload-file-list-item";
import { UploadFile } from "../../../../../models/classes/upload-file.class";
import { IProgressedFileResult } from "../../../../../models/interfaces/progressed-file-result.interface";
import { FileListItemComponent } from "./FileListItem/FileListItem";

import "./FileList.scss";

import { downloadBlobFile } from "../../../../../../utils/functions/downloadBlobFile";

interface IFileListProps {
  uploadFiles: UploadFile[];
  uploadTemplate?: (
    uploadFile: UploadFile,
    setIsFileProcessing: (id: string, isProcessing: boolean) => void,
    downloadFunction: (file: UploadFile, content: IProgressedFileResult) => void,
  ) => ReactNode;
  handleRemoveFile: (file: UploadFile) => void;
}

export const FileListComponent: FunctionComponent<IFileListProps> = (props: IFileListProps) => {
  const { t } = useTranslation();

  const [uploadFileListItems, setUploadFileListItems] = useState<UploadFileListItem[]>([]);

  useEffect(() => {
    setUploadFileListItems(prevs => {
      const progressById = new Map(prevs.map(item => [item.uploadFile.id, item.isProgressing]));
      return props.uploadFiles.map(
        uploadFile =>
          new UploadFileListItem({
            uploadFile,
            isProgressing: progressById.get(uploadFile.id) ?? false,
          }),
      );
    });
  }, [props.uploadFiles]);

  const downloadFunction = (uploadFile: UploadFile, content: IProgressedFileResult) => {
    const output: string = content.successMessage ?? "";
    const blob = new Blob([output], { type: "text/plain" });

    const filename: string = `${t(translationKeys.upload.summary.downloadFileNamePrefix) + uploadFile.file.name}.txt`;
    downloadBlobFile(blob, filename);
  };

  const handleRemoveFile = (uploadFile: UploadFile) => {
    if (!uploadFile.isProgressing) {
      props.handleRemoveFile(uploadFile);
    }
  };

  const handleSetIsFileProgressing = (id: any, isProgressing: boolean) => {
    setUploadFileListItems(prevs =>
      prevs.map(item =>
        item.uploadFile.id === id ? new UploadFileListItem({ uploadFile: item.uploadFile, isProgressing }) : item,
      ),
    );
  };

  if (props.uploadFiles?.length < 1) {
    return;
  }

  return (
    <div className="file-list-container">
      {uploadFileListItems.map(uploadFileListItem => (
        <FileListItemComponent
          key={uploadFileListItem.uploadFile.id}
          uploadFileListItem={uploadFileListItem}
          handleRemoveFile={handleRemoveFile}
          downloadFunction={downloadFunction}
          uploadTemplate={props.uploadTemplate}
          setIsFileProcessing={(id: string, isFileProcessing: boolean) =>
            handleSetIsFileProgressing(id, isFileProcessing)
          }
        />
      ))}
    </div>
  );
};
